import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Check if a portal role is authorized for the given constraints.
 * Returns 'allow' | 'deny' | null (null = no role at all).
 */
export function checkPortalAccess(portalRole, { allowedRoles, clubId } = {}) {
  if (!portalRole) return null;

  // GlobalAdmin always passes
  if (portalRole.role === 'GlobalAdmin') return 'allow';

  // Check allowed roles
  if (allowedRoles && !allowedRoles.includes(portalRole.role)) return 'deny';

  // Check club scope if required
  if (clubId && portalRole.clubId !== clubId) return 'deny';

  return 'allow';
}

/**
 * Route guard that requires authentication and optionally a portal role.
 *
 * Props:
 *   allowedRoles - array of portal roles that can access this route (optional).
 *                  If omitted, any authenticated user with a portal role can access.
 *   clubId      - if provided, ClubAdmin/RaceOfficer must be scoped to this club.
 */
export default function RequireAuth({ children, allowedRoles, clubId }) {
  const { user, loading, portalRole } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="page"><p>Loading...</p></div>;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const access = checkPortalAccess(portalRole, { allowedRoles, clubId });

  if (access !== 'allow') {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
}
